import { useMemo, useState } from "react";

import {
  Box,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  Typography,
} from "@material-ui/core";

import { QUERIES } from "../../../graphql";
import {
  Building,
  ServiceBuilding,
  ServiceOverridableProps,
} from "../../../graphql/models";
import { useSnackbarQuery } from "../../../hooks";
import { ServiceBuildingEditor } from "../service_building_editor";

import useServiceBuildingsPickerStyle from "./service_buildings_picker.style";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

export interface ServiceBuildingsPickerProps {
  defaultOverridableProps: ServiceOverridableProps;
  askCompatiblePlaces?: boolean;
  buildings: ServiceBuilding[];
  onChange: (buildings: ServiceBuilding[]) => void;
}

export function ServiceBuildingsPicker(props: ServiceBuildingsPickerProps) {
  const {
    buildings,
    defaultOverridableProps,
    onChange,
    askCompatiblePlaces = false,
  } = props;
  const [selectedIndex, setSelectedIndex] = useState(0);
  const classes = useServiceBuildingsPickerStyle();

  const { data, loading } = useSnackbarQuery(QUERIES.BUILDINGS("light"));

  const remoteBuildings: Building[] = useMemo(() => {
    return data?.buildings ?? [];
  }, [data]);

  const onBuildingUpdated = (serviceBuilding: ServiceBuilding) => {
    const buildingsCopy = [...buildings];
    const serviceBuildingIndex = buildingsCopy.findIndex(
      (v) => v.id === serviceBuilding.id
    );

    if (serviceBuildingIndex > -1) {
      buildingsCopy.splice(serviceBuildingIndex, 1, {
        ...serviceBuilding,
      });
    } else {
      buildingsCopy.push({
        ...serviceBuilding,
      });
    }

    onChange([...buildingsCopy]);
  };

  return (
    <>
      {loading && <CircularProgress />}
      {!loading && (
        <Paper className={classes.root}>
          <Tabs
            className={classes.tabs}
            orientation="vertical"
            variant="scrollable"
            value={selectedIndex}
            onChange={(_, v) => setSelectedIndex(v)}
          >
            {remoteBuildings.map((v: Building, i: number) => (
              <Tab label={v.name} />
            ))}
          </Tabs>
          {remoteBuildings.map((remoteBuilding: Building, i: number) => {
            const compiledBuildingRef =
              buildings.find((v) => v.id === remoteBuilding.id) ??
              remoteBuilding;

            return (
              <TabPanel value={selectedIndex} index={i}>
                <ServiceBuildingEditor
                  defaultOverridableProps={defaultOverridableProps}
                  building={compiledBuildingRef}
                  askCompatiblePlaces={askCompatiblePlaces}
                  onChange={(props, compatiblePlaces) =>
                    onBuildingUpdated({
                      ...compiledBuildingRef,
                      compatiblePlaces,
                      overrideProps: {
                        ...props,
                      },
                    })
                  }
                />
              </TabPanel>
            );
          })}
        </Paper>
      )}
    </>
  );
}
